<template>
    <div class="editable-goal-table-wrapper" @paste="handlePaste">
        <table class="table editable-goal-table">
            <thead>
                <tr class="table-header-row">
                    <th>Metric</th>
                    <th v-for="month in months" :key="month">{{ month }}</th>
                    <th>Total</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(metric, rowIndex) in metrics" :key="metric.key">
                    <td class="metric-label">{{ metric.label }}</td>
                    <td 
                        v-for="(month, colIndex) in months" 
                        :key="month"
                        :class="{
                            'editable-cell': !readOnly,
                            'invalid-cell': isCellInvalid(metric.key, month),
                            'focused': focusedRow === rowIndex && focusedCol === colIndex
                        }"
                        @click="!readOnly && startEdit(rowIndex, colIndex, metric.key, month)"
                        @keydown="handleKeyDown($event, rowIndex, colIndex, metric.key, month)"
                        tabindex="0"
                        :ref="el => setCellRef(el, rowIndex, colIndex)"
                    >
                        <input
                            v-if="editingRow === rowIndex && editingCol === colIndex"
                            type="text"
                            :value="editValue"
                            @input="updateEditValue($event.target.value)"
                            @blur="finishEdit(metric.key, month)"
                            @keydown.enter.prevent="confirmEdit(rowIndex, colIndex, metric.key, month)"
                            @keydown.escape.prevent="cancelEdit"
                            @keydown.tab.prevent="handleTab($event, rowIndex, colIndex, metric.key, month)"
                            :ref="el => editInputRef = el"
                            class="cell-input"
                            aria-label="Text input"
                        />
                        <span v-else class="cell-display">
                            {{ formatCellValue(metric.key, month, rowIndex) }}
                        </span>
                    </td>
                    <td class="total-cell">
                        <strong>{{ formatTotal(metric.key, rowIndex) }}</strong>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
    name: 'EditableGoalTable',
    props: {
        metrics: {
            type: Array,
            required: true
        },
        months: {
            type: Array,
            required: true
        },
        goalData: {
            type: Object,
            required: true
        },
        readOnly: {
            type: Boolean,
            default: false
        },
        invalidCells: {
            type: Array,
            default: () => []
        }
    },
    emits: ['cell-change', 'cell-blur'],
    data() {
        return {
            editingRow: null,
            editingCol: null,
            editValue: '',
            originalValue: '',
            focusedRow: null,
            focusedCol: null,
            cellRefs: {},
            editInputRef: null
        };
    },
    methods: {
        setCellRef(el, row, col) {
            if (el) {
                const key = `${row}-${col}`;
                this.cellRefs[key] = el;
            }
        },
        getCellRef(row, col) {
            return this.cellRefs[`${row}-${col}`];
        },
        isCellInvalid(metricKey, month) {
            return this.invalidCells.some(cell => 
                cell.metricKey === metricKey && cell.month === month
            );
        },
        getCellValue(metricKey, month) {
            return this.goalData?.[metricKey]?.[month] || 0;
        },
        formatCellValue(metricKey, month, rowIndex) {
            const value = this.getCellValue(metricKey, month);
            if (rowIndex === 0) {
                // Revenue row - currency format
                return this.formatCurrency(value);
            } else {
                // Other rows - number format
                return this.formatNumber(value);
            }
        },
        formatTotal(metricKey, rowIndex) {
            const total = this.months.reduce((sum, month) => {
                return sum + (Number(this.getCellValue(metricKey, month)) || 0);
            }, 0);
            
            if (rowIndex === 0) {
                return this.formatCurrency(total);
            } else {
                return this.formatNumber(total);
            }
        },
        formatNumber(value) {
            return new Intl.NumberFormat().format(value);
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(value);
        },
        parseFormattedNumber(value) {
            if (value === null || value === '') return 0;
            if (typeof value === 'number') return value;
            
            const strValue = String(value);
            const cleanedValue = strValue.replace(/[^0-9.-]/g, '');
            return Number(cleanedValue);
        },
        startEdit(row, col, metricKey, month) {
            if (this.readOnly) return;
            
            this.editingRow = row;
            this.editingCol = col;
            this.focusedRow = row;
            this.focusedCol = col;
            
            const rawValue = this.getCellValue(metricKey, month);
            this.editValue = rawValue.toString();
            this.originalValue = rawValue.toString();
            
            this.$nextTick(() => {
                if (this.editInputRef) {
                    this.editInputRef.focus();
                    this.editInputRef.select();
                }
            });
        },
        updateEditValue(value) {
            this.editValue = value;
        },
        finishEdit(metricKey, month) {
            if (this.editingRow === null) return;
            
            const parsedValue = this.parseFormattedNumber(this.editValue);
            
            this.$emit('cell-change', {
                metricKey,
                month,
                value: this.editValue
            });
            
            this.editingRow = null;
            this.editingCol = null;
            this.editValue = '';
            this.originalValue = '';
            
            this.$emit('cell-blur');
        },
        cancelEdit() {
            this.editingRow = null;
            this.editingCol = null;
            this.editValue = '';
            this.originalValue = '';
        },
        confirmEdit(row, col, metricKey, month) {
            this.finishEdit(metricKey, month);
            // Move down after confirming with Enter
            this.moveToCell(row + 1, col);
        },
        handleTab(event, row, col, metricKey, month) {
            this.finishEdit(metricKey, month);
            
            if (event.shiftKey) {
                // Shift+Tab - move to previous cell
                this.moveToPreviousCell(row, col);
            } else {
                // Tab - move to next cell
                this.moveToNextCell(row, col);
            }
        },
        handleKeyDown(event, row, col, metricKey, month) {
            // If already editing, let the input handle it
            if (this.editingRow === row && this.editingCol === col) {
                return;
            }
            
            const key = event.key;
            
            // Arrow key navigation
            if (key === 'ArrowUp') {
                event.preventDefault();
                this.moveToCell(row - 1, col);
            } else if (key === 'ArrowDown') {
                event.preventDefault();
                this.moveToCell(row + 1, col);
            } else if (key === 'ArrowLeft') {
                event.preventDefault();
                this.moveToCell(row, col - 1);
            } else if (key === 'ArrowRight') {
                event.preventDefault();
                this.moveToCell(row, col + 1);
            } else if (key === 'Enter') {
                event.preventDefault();
                this.startEdit(row, col, metricKey, month);
            } else if (key === 'Tab') {
                event.preventDefault();
                if (event.shiftKey) {
                    this.moveToPreviousCell(row, col);
                } else {
                    this.moveToNextCell(row, col);
                }
            } else if (!this.readOnly && key.length === 1 && !event.ctrlKey && !event.metaKey) {
                // Start editing if a character key is pressed
                this.startEdit(row, col, metricKey, month);
            }
        },
        moveToCell(row, col) {
            // Boundary checks
            if (row < 0 || row >= this.metrics.length) return;
            if (col < 0 || col >= this.months.length) return;
            
            this.focusedRow = row;
            this.focusedCol = col;
            
            const cellRef = this.getCellRef(row, col);
            if (cellRef) {
                cellRef.focus();
            }
        },
        moveToNextCell(row, col) {
            let nextCol = col + 1;
            let nextRow = row;
            
            if (nextCol >= this.months.length) {
                nextCol = 0;
                nextRow = row + 1;
            }
            
            if (nextRow < this.metrics.length) {
                this.moveToCell(nextRow, nextCol);
            }
        },
        moveToPreviousCell(row, col) {
            let prevCol = col - 1;
            let prevRow = row;
            
            if (prevCol < 0) {
                prevCol = this.months.length - 1;
                prevRow = row - 1;
            }
            
            if (prevRow >= 0) {
                this.moveToCell(prevRow, prevCol);
            }
        },
        handlePaste(event) {
            if (this.readOnly) return;
            if (this.focusedRow === null || this.focusedCol === null) return;
            
            event.preventDefault();
            
            const clipboardData = event.clipboardData || window.clipboardData;
            const pastedText = clipboardData.getData('text');
            
            // Parse pasted data (tab-delimited columns, newline-delimited rows)
            const rows = pastedText.split(/\r?\n/).filter(row => row.trim() !== '');
            
            let currentRow = this.focusedRow;
            
            rows.forEach((rowData) => {
                const values = rowData.split('\t');
                let currentCol = this.focusedCol;
                
                values.forEach((value) => {
                    if (currentRow >= this.metrics.length || currentCol >= this.months.length) {
                        return;
                    }
                    
                    const metric = this.metrics[currentRow];
                    const month = this.months[currentCol];
                    
                    // Emit cell change for each pasted cell
                    this.$emit('cell-change', {
                        metricKey: metric.key,
                        month: month,
                        value: value.trim()
                    });
                    
                    currentCol++;
                });
                
                currentRow++;
            });
            
            this.$emit('cell-blur');
        }
    }
});
</script>

<style scoped>
.editable-goal-table-wrapper {
    width: 100%;
    overflow-x: auto;
}

.editable-goal-table {
    width: 100%;
    border-collapse: collapse;
    margin: 0;
}

.editable-goal-table thead tr {
    background-color: #f8f9fa;
}

.editable-goal-table th {
    padding: 12px 8px;
    text-align: left;
    font-weight: 600;
    border: 1px solid #dee2e6;
    white-space: nowrap;
}

.editable-goal-table td {
    padding: 0;
    border: 1px solid #dee2e6;
    position: relative;
}

.metric-label {
    font-weight: 500;
    padding: 8px 12px !important;
    background-color: #f8f9fa;
}

.editable-cell {
    cursor: pointer;
    min-height: 40px;
    padding: 8px 12px;
}

.editable-cell:hover {
    background-color: #f0f0f0;
}

.editable-cell.focused {
    outline: 2px solid #0d6efd;
    outline-offset: -2px;
}

.cell-display {
    display: block;
    padding: 8px 12px;
    min-height: 20px;
}

.cell-input {
    width: 100%;
    border: none;
    outline: none;
    padding: 8px 12px;
    font-size: inherit;
    font-family: inherit;
    background: white;
    box-sizing: border-box;
}

.total-cell {
    background-color: #f8f9fa;
    padding: 8px 12px !important;
    text-align: right;
}

.invalid-cell {
    background-color: #ffebee !important;
    border: 2px solid #dc3545 !important;
}

.invalid-cell .cell-input {
    background-color: #ffebee;
}

/* Responsive */
@media (max-width: 768px) {
    .editable-goal-table th,
    .editable-goal-table td {
        padding: 6px 4px;
        font-size: 0.9rem;
    }
    
    .cell-display,
    .cell-input {
        padding: 6px 4px;
    }
}
</style>

